import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClientCard = ({ 
  client, 
  onEdit, 
  onViewHistory, 
  onToggleStatus, 
  onAssignPlan,
  onViewEvaluation 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'inactive': return 'text-muted-foreground bg-muted/30';
      case 'suspended': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted/30';
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatLastTraining = (lastTraining) => {
    if (!lastTraining) return 'Never';
    
    const date = new Date(lastTraining);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-6 hover:shadow-md transition-all duration-200">
      <div className="flex items-start space-x-4">
        {/* Client Photo */}
        <div className="flex-shrink-0">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
            {client?.photo ? (
              <Image
                src={client?.photo}
                alt={`${client?.name} profile photo`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="User" size={24} className="text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Client Information */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {client?.name}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>Age: {calculateAge(client?.dateOfBirth)}</span>
                <span>•</span>
                <span>RFID: {client?.rfidId}</span>
              </div>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(client?.status)}`}>
              {client?.status?.charAt(0)?.toUpperCase() + client?.status?.slice(1)}
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <p className="text-muted-foreground">Active Plans</p>
              <p className="font-medium text-foreground">{client?.activePlans || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Training</p>
              <p className="font-medium text-foreground">
                {formatLastTraining(client?.lastTraining)}
              </p>
            </div>
          </div>

          {/* Text Identifier */}
          {client?.textIdentifier && (
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-1">Identifier</p>
              <p className="text-sm text-foreground bg-muted/30 px-3 py-1 rounded-md">
                {client?.textIdentifier}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(client)}
              iconName="Edit"
              iconPosition="left"
              iconSize={16}
            >
              Edit
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewHistory(client)}
              iconName="History"
              iconPosition="left"
              iconSize={16}
            >
              History
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAssignPlan(client)}
              iconName="FileText"
              iconPosition="left"
              iconSize={16}
            >
              Assign Plan
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewEvaluation(client)}
              iconName="BarChart3"
              iconPosition="left"
              iconSize={16}
            >
              Evaluation
            </Button>
            
            <Button
              variant={client?.status === 'active' ? 'warning' : 'success'}
              size="sm"
              onClick={() => onToggleStatus(client)}
              iconName={client?.status === 'active' ? 'UserX' : 'UserCheck'}
              iconPosition="left"
              iconSize={16}
            >
              {client?.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientCard;